// Single source of truth for all editable site content, theme and section order.
// The published config from the API is deep-merged OVER these defaults.

export const FONT_OPTIONS = [
  "Outfit", "Plus Jakarta Sans", "Inter", "Sora", "Manrope", "Space Grotesk",
  "DM Sans", "Poppins", "Montserrat", "Bricolage Grotesque", "Instrument Sans", "Figtree",
];

export const DEFAULT_CONFIG = {
  theme: {
    brand: "#0055FF",
    brandDark: "#003399",
    ai: "#34C759",
    sos: "#FF3B30",
    headingFont: "Outfit",
    bodyFont: "Plus Jakarta Sans",
  },
  t: {
    hero: {
      badge: "Be one of the first to experience SOSConnect AI",
      line1: "Help is now",
      line2: "intelligent.",
      paragraph: "SOSConnect AI connects you with nearby trusted help when life catches you off guard. From roadside emergencies to problems at home, help is only a few taps away.",
      ctaPrimary: "Get Early Access",
      ctaSecondary: "See How It Works",
      formTitle: "Get early access",
      formSubtitle: "Join the waitlist and help decide where we launch first.",
      supporting: "Be one of the first to experience SOSConnect AI.",
    },
    problem: {
      eyebrow: "01 — The problem",
      heading: "Getting help shouldn't be complicated.",
      paragraph: "When something goes wrong, people usually start searching Google, calling multiple businesses, comparing reviews, explaining the problem repeatedly, and hoping someone is available. SOSConnect AI is designed to simplify that entire process.",
    },
    howItWorks: { eyebrow: "02 — How it works", heading: "Help in a few simple steps." },
    video: { eyebrow: "03 — See the app", heading: "See SOSConnect AI in action.", paragraph: "One app designed to make finding help faster, easier, and smarter." },
    features: { eyebrow: "04 — Features", heading: "Built for moments when every step matters." },
    services: { eyebrow: "05 — Services", heading: "One platform. Help for life's unexpected moments." },
    aiDemo: { eyebrow: "06 — Intelligence", heading: "AI that helps understand what you need." },
    liveService: { eyebrow: "07 — Live experience", heading: "Your provider is on the way.", paragraph: "SOSConnect AI isn't a business directory. It manages your entire service experience — from request to resolution." },
    launch: { eyebrow: "08 — Where next", heading: "Where should SOSConnect AI launch next?", paragraph: "Tell us where you'd like SOSConnect AI available. The cities with the strongest demand will help shape where we launch first.", formTitle: "Request your city", formSubtitle: "Add your city to the map and help us decide where to launch first." },
    leaderboard: { eyebrow: "09 — The leaderboard", heading: "Which city gets SOSConnect AI first?", paragraph: "The more people who request your city, the higher it moves.", cta: "Move Your City Up", note: "Share SOSConnect AI with friends in your city to climb the list." },
    provider: { badge: "For Providers", heading: "Grow your business with SOSConnect AI.", paragraph: "Connect with nearby customers who are actively looking for the services your business provides.", cta: "Become a Provider" },
    betterWay: { eyebrow: "10 — The difference", heading: "A better way to find help." },
    finalCta: { heading: "Be there from the beginning.", paragraph: "SOSConnect AI is coming soon. Join the early access list and help decide where we launch first.", note: "No spam. Just important SOSConnect AI launch updates." },
    faq: { eyebrow: "FAQ", heading: "Questions, answered." },
    footer: { tagline: "Help is now intelligent.", copyright: "© 2026 SOSConnect AI. All rights reserved." },
  },
  lists: {
    problems: ["Flat tire", "Dead battery", "Vehicle lockout", "Home lockout", "Plumbing leak", "Need a locksmith", "Emergency repair"],
    steps: [
      { title: "Tell us what's wrong", body: "Select your problem or describe what happened in your own words.", screen: "chat" },
      { title: "SOSConnect AI understands", body: "AI identifies the service you need and guides you through the request.", screen: "ai" },
      { title: "Find nearby help", body: "We connect you with relevant, trusted providers close to you.", screen: "providers" },
      { title: "Stay updated", body: "Follow your request live and see real-time provider updates.", screen: "tracking" },
      { title: "Problem solved", body: "Complete the service, handle payment, and leave feedback.", screen: "done" },
    ],
    features: [
      { title: "AI-Powered Assistance", body: "Explain what happened naturally and SOSConnect AI guides you to the right type of service.", tag: "AI" },
      { title: "Nearby Providers", body: "Discover trusted providers relevant to your problem and your exact location.", tag: "Live" },
      { title: "Live Service Updates", body: "Stay informed while a provider is on the way and responding to your request.", tag: "Live" },
      { title: "Trusted Reviews", body: "View ratings and real customer feedback before selecting who helps you.", tag: "" },
      { title: "Multiple Services", body: "Roadside, locksmiths, plumbing, home services and more — one platform.", tag: "" },
      { title: "Simple Payments", body: "A streamlined service and payment experience without unnecessary steps.", tag: "" },
    ],
    services: ["Roadside Assistance", "Towing", "Flat Tire Help", "Jump Starts", "Vehicle Lockouts", "Locksmiths", "Home Lockouts", "Plumbing", "Leaks", "Emergency Home Services"],
    providerBenefits: [
      "Receive nearby service opportunities",
      "Reach customers when they actually need your service",
      "Manage incoming requests",
      "Assign jobs to your workers",
      "Build your reputation through reviews",
      "Grow your customer base",
      "Join new markets as SOSConnect AI expands",
    ],
    comparison: {
      old: ["Search Google", "Call multiple businesses", "Ask if someone is available", "Explain the problem repeatedly", "Wait for callbacks", "Compare businesses manually"],
      new: ["Explain the problem once", "Discover relevant nearby help", "Compare providers", "Stay updated in real time", "Manage your service from one place"],
    },
    marquee: ["Roadside", "Locksmith", "Plumbing", "Home Services", "Towing", "Lockouts", "Leak Repair"],
    faqs: [
      { q: "What is SOSConnect AI?", a: "SOSConnect AI is an AI-powered emergency services marketplace that helps you quickly find and connect with nearby trusted providers for roadside emergencies, lockouts, plumbing, home services and other urgent everyday problems." },
      { q: "How does SOSConnect AI work?", a: "You describe what's wrong, our AI identifies the type of help you need, and we connect you with relevant nearby providers. You then track the service live from request to completion." },
      { q: "When will SOSConnect AI launch?", a: "We're launching soon. Join the early access list to be notified the moment SOSConnect AI is available near you." },
      { q: "Where will SOSConnect AI launch first?", a: "We launch first in the cities with the strongest combined customer and provider demand. Request your city to help move it up the list." },
      { q: "How can I request my city?", a: "Use any of the location forms on this page to add your city. The more people who request your city, the higher it ranks on our launch leaderboard." },
      { q: "What services will SOSConnect AI support?", a: "Roadside assistance, towing, flat tire help, jump starts, vehicle and home lockouts, locksmiths, plumbing, leak repair and emergency home services — with more categories on the way." },
      { q: "How does SOSConnect AI find providers?", a: "We match your request and location with trusted providers whose services and coverage area fit your situation." },
      { q: "Does SOSConnect AI provide the services itself?", a: "No. SOSConnect AI is a platform that connects you with independent, vetted service providers — it helps manage the entire experience end to end." },
      { q: "How can my business become a provider?", a: "Visit our provider application page and apply to join. Approved businesses are among the first available when we launch in their area." },
      { q: "Will SOSConnect AI be available on iPhone and Android?", a: "Yes. SOSConnect AI is being built for both iPhone and Android." },
    ],
  },
  sections: [
    { id: "social", label: "Social Proof", visible: true },
    { id: "problem", label: "Problem", visible: true },
    { id: "howItWorks", label: "How It Works", visible: true },
    { id: "video", label: "Video", visible: true },
    { id: "features", label: "Features", visible: true },
    { id: "services", label: "Services", visible: true },
    { id: "aiDemo", label: "AI Demonstration", visible: true },
    { id: "liveService", label: "Live Service", visible: true },
    { id: "marquee", label: "Marquee", visible: true },
    { id: "launch", label: "Launch Location + Map", visible: true },
    { id: "leaderboard", label: "City Leaderboard", visible: true },
    { id: "provider", label: "For Providers", visible: true },
    { id: "betterWay", label: "Old vs New", visible: true },
    { id: "finalCta", label: "Final CTA", visible: true },
    { id: "faq", label: "FAQ", visible: true },
  ],
};

// Deep-merge published/draft data over defaults (objects merge, arrays replace).
export function mergeConfig(base, over) {
  if (!over || typeof over !== "object") return base;
  if (Array.isArray(base)) return Array.isArray(over) ? over : base;
  const out = { ...base };
  for (const k of Object.keys(over)) {
    if (base[k] && typeof base[k] === "object" && !Array.isArray(base[k]) && typeof over[k] === "object" && !Array.isArray(over[k])) {
      out[k] = mergeConfig(base[k], over[k]);
    } else if (over[k] !== undefined) {
      out[k] = over[k];
    }
  }
  return out;
}
